/* @ts-self-types="./rewild_wasm.d.ts" */

/**
 * A built BVH. Take each array once, then call `free()`.
 */
export class BvhBuild {
    static __wrap(ptr) {
        const obj = Object.create(BvhBuild.prototype);
        obj.__wbg_ptr = ptr;
        BvhBuildFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        BvhBuildFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_bvhbuild_free(ptr, 0);
    }
    /**
     * The nodes, in the layout `BVHTree` reads. Copied into a new
     * `Uint32Array`. Empty after the first call.
     * @returns {Uint32Array}
     */
    takeNodes() {
        const ret = wasm.bvhbuild_takeNodes(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The reordered triangle indices. Copied into a new `Uint32Array`. Empty
     * after the first call.
     * @returns {Uint32Array}
     */
    takeTriIndices() {
        const ret = wasm.bvhbuild_takeTriIndices(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
}
if (Symbol.dispose) BvhBuild.prototype[Symbol.dispose] = BvhBuild.prototype.free;

/**
 * A far map for the horizon, built a few rows at a time. See
 * `terrain::FarMap`. Take its arrays once it is done, then call `free()`.
 */
export class FarMapBuild {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FarMapBuildFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_farmapbuild_free(ptr, 0);
    }
    /**
     * Builds up to `rows` more rows of `generator`'s world; returns whether
     * the map is complete.
     * @param {TerrainGenerator} generator
     * @param {number} rows
     * @returns {boolean}
     */
    build(generator, rows) {
        _assertClass(generator, TerrainGenerator);
        const ret = wasm.farmapbuild_build(this.__wbg_ptr, generator.__wbg_ptr, rows);
        return ret !== 0;
    }
    /**
     * A map of `texels`² over `span` metres centred on world (`centreX`,
     * `centreZ`).
     * @param {number} centre_x
     * @param {number} centre_z
     * @param {number} span
     * @param {number} texels
     */
    constructor(centre_x, centre_z, span, texels) {
        const ret = wasm.farmapbuild_new(centre_x, centre_z, span, texels);
        this.__wbg_ptr = ret;
        FarMapBuildFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * rg16float bits per texel: height, then continent.
     * @returns {Uint16Array}
     */
    takeGround() {
        const ret = wasm.farmapbuild_takeGround(this.__wbg_ptr);
        var v1 = getArrayU16FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 2, 2);
        return v1;
    }
    /**
     * Palette weights as two RGBA8 planes.
     * @returns {Uint8Array}
     */
    takeWeights() {
        const ret = wasm.farmapbuild_takeWeights(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) FarMapBuild.prototype[Symbol.dispose] = FarMapBuild.prototype.free;

/**
 * A chunk's resolved water. Take each array once, then call `free()`.
 */
export class ResolvedWaterBuild {
    static __wrap(ptr) {
        const obj = Object.create(ResolvedWaterBuild.prototype);
        obj.__wbg_ptr = ptr;
        ResolvedWaterBuildFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ResolvedWaterBuildFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_resolvedwaterbuild_free(ptr, 0);
    }
    /**
     * @returns {boolean}
     */
    get covered() {
        const ret = wasm.resolvedwaterbuild_covered(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The generated lakes that reach the chunk, as `Lake` records.
     * @returns {any}
     */
    lakes() {
        const ret = wasm.resolvedwaterbuild_lakes(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {Uint32Array}
     */
    takeBodyIds() {
        const ret = wasm.resolvedwaterbuild_takeBodyIds(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * @returns {Uint8Array}
     */
    takeCoverage() {
        const ret = wasm.resolvedwaterbuild_takeCoverage(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {Float64Array}
     */
    takeLevels() {
        const ret = wasm.resolvedwaterbuild_takeLevels(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * @returns {Uint8Array}
     */
    takeTypeWeights() {
        const ret = wasm.resolvedwaterbuild_takeTypeWeights(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ResolvedWaterBuild.prototype[Symbol.dispose] = ResolvedWaterBuild.prototype.free;

/**
 * A chunk's scatter, one entry per layer that placed anything, in slot
 * order. Take each layer's arrays once, then call `free()`.
 */
export class ScatterBuild {
    static __wrap(ptr) {
        const obj = Object.create(ScatterBuild.prototype);
        obj.__wbg_ptr = ptr;
        ScatterBuildFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ScatterBuildFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_scatterbuild_free(ptr, 0);
    }
    /**
     * Entry `i`'s instance count.
     * @param {number} i
     * @returns {number}
     */
    count(i) {
        const ret = wasm.scatterbuild_count(this.__wbg_ptr, i);
        return ret >>> 0;
    }
    /**
     * @returns {number}
     */
    get layerCount() {
        const ret = wasm.scatterbuild_layerCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Entry `i`'s library slot.
     * @param {number} i
     * @returns {number}
     */
    slot(i) {
        const ret = wasm.scatterbuild_slot(this.__wbg_ptr, i);
        return ret >>> 0;
    }
    /**
     * Entry `i`'s instances, `INSTANCE_STRIDE` floats each.
     * @param {number} i
     * @returns {Float32Array}
     */
    takeData(i) {
        const ret = wasm.scatterbuild_takeData(this.__wbg_ptr, i);
        var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Entry `i`'s kill keys; empty unless asked for.
     * @param {number} i
     * @returns {Uint32Array}
     */
    takeIds(i) {
        const ret = wasm.scatterbuild_takeIds(this.__wbg_ptr, i);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
}
if (Symbol.dispose) ScatterBuild.prototype[Symbol.dispose] = ScatterBuild.prototype.free;

/**
 * What a chunk adds to scatter placement. See `terrain::ScatterRequest`.
 */
export class ScatterRequestData {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ScatterRequestDataFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_scatterrequestdata_free(ptr, 0);
    }
    constructor() {
        const ret = wasm.scatterrequestdata_new();
        this.__wbg_ptr = ret;
        ScatterRequestDataFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The chunk's painted biomes, channel-major planes.
     * @param {Uint8Array} weights
     * @param {number} size
     * @param {number} step
     */
    setBiomeMask(weights, size, step) {
        const ptr0 = passArray8ToWasm0(weights, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.scatterrequestdata_setBiomeMask(this.__wbg_ptr, ptr0, len0, size, step);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * The chunk's painted scatter density, channel-major planes.
     * @param {Uint8Array} weights
     * @param {number} size
     * @param {number} step
     */
    setDensityMask(weights, size, step) {
        const ptr0 = passArray8ToWasm0(weights, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.scatterrequestdata_setDensityMask(this.__wbg_ptr, ptr0, len0, size, step);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Kill keys of the chunk's plucked instances, in any order.
     * @param {Uint32Array} keys
     */
    setKills(keys) {
        const ptr0 = passArray32ToWasm0(keys, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.scatterrequestdata_setKills(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Places only the cells overlapping chunk samples (x0, y0)–(x1, y1).
     * @param {number} x0
     * @param {number} y0
     * @param {number} x1
     * @param {number} y1
     */
    setRegion(x0, y0, x1, y1) {
        wasm.scatterrequestdata_setRegion(this.__wbg_ptr, x0, y0, x1, y1);
    }
    /**
     * @param {WaterEditData} edit
     */
    setWaterEdit(edit) {
        _assertClass(edit, WaterEditData);
        var ptr0 = edit.__destroy_into_raw();
        wasm.scatterrequestdata_setWaterEdit(this.__wbg_ptr, ptr0);
    }
    /**
     * Report each instance's kill key.
     * @param {boolean} with_ids
     */
    setWithIds(with_ids) {
        wasm.scatterrequestdata_setWithIds(this.__wbg_ptr, with_ids);
    }
}
if (Symbol.dispose) ScatterRequestData.prototype[Symbol.dispose] = ScatterRequestData.prototype.free;

/**
 * Terrain generation for one world. Build once per climate preset, seed and
 * sea level, then generate chunks and sample points from it.
 */
export class TerrainGenerator {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        TerrainGeneratorFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_terraingenerator_free(ptr, 0);
    }
    /**
     * The biomes at lake-space (u, v), as a chunk resolves them, into
     * `biomes` and `weights`; returns how many. `maskWeights` is the painted
     * biome mask of the chunk there, if any (empty for none), and (`localX`,
     * `localY`) the point's sample in that chunk. Each output holds the
     * climate's biome count plus four.
     * @param {number} u
     * @param {number} v
     * @param {Uint8Array} mask_weights
     * @param {number} mask_size
     * @param {number} mask_step
     * @param {number} local_x
     * @param {number} local_y
     * @param {Int32Array} biomes
     * @param {Float64Array} weights
     * @returns {number}
     */
    biomesAt(u, v, mask_weights, mask_size, mask_step, local_x, local_y, biomes, weights) {
        const ptr0 = passArray8ToWasm0(mask_weights, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = passArray32ToWasm0(biomes, wasm.__wbindgen_malloc);
        var len1 = WASM_VECTOR_LEN;
        var ptr2 = passArrayF64ToWasm0(weights, wasm.__wbindgen_malloc);
        var len2 = WASM_VECTOR_LEN;
        const ret = wasm.terraingenerator_biomesAt(this.__wbg_ptr, u, v, ptr0, len0, mask_size, mask_step, local_x, local_y, ptr1, len1, biomes, ptr2, len2, weights);
        return ret >>> 0;
    }
    /**
     * The un-eroded, uncarved ground at lake-space (u, v).
     * @param {number} u
     * @param {number} v
     * @returns {number}
     */
    ground(u, v) {
        const ret = wasm.terraingenerator_ground(this.__wbg_ptr, u, v);
        return ret;
    }
    /**
     * A `width` × `height` height field at chunk offset (`offsetX`,
     * `offsetY`), with lakes carved. See `terrain::HeightGenerator`.
     * @param {number} width
     * @param {number} height
     * @param {number} offset_x
     * @param {number} offset_y
     * @returns {Float32Array}
     */
    heights(width, height, offset_x, offset_y) {
        const ret = wasm.terraingenerator_heights(this.__wbg_ptr, width, height, offset_x, offset_y);
        var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * What every lake cell overlapping the lake-space box did, as
     * `LakeCellReport` records. Slow; for debugging.
     * @param {number} u0
     * @param {number} v0
     * @param {number} u1
     * @param {number} v1
     * @returns {any}
     */
    inspectLakes(u0, v0, u1, v1) {
        const ret = wasm.terraingenerator_inspectLakes(this.__wbg_ptr, u0, v0, u1, v1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Every lake whose bank reaches the lake-space box [u0, u1] × [v0, v1],
     * as `Lake` records.
     * @param {number} u0
     * @param {number} v0
     * @param {number} u1
     * @param {number} v1
     * @returns {any}
     */
    lakes(u0, v0, u1, v1) {
        const ret = wasm.terraingenerator_lakes(this.__wbg_ptr, u0, v0, u1, v1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * `climate` is a `ClimateConfig`; `splat` is `{ palette, coastMaterials }`
     * (see `terrain::SplatSpec`); `scatterLayers` is the scatter layer library
     * in slot order. Throws if any is invalid.
     * @param {any} climate
     * @param {any} splat
     * @param {any} scatter_layers
     * @param {number} seed
     * @param {number} sea_level
     * @param {number} meters_per_sample
     */
    constructor(climate, splat, scatter_layers, seed, sea_level, meters_per_sample) {
        const ret = wasm.terraingenerator_new(climate, splat, scatter_layers, seed, sea_level, meters_per_sample);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        TerrainGeneratorFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Every scatter instance a `chunkSize` chunk at chunk offset (`offsetX`,
     * `offsetY`) with LOD-0 `heights` carries. See
     * `terrain::ScatterGenerator::generate`.
     * @param {Float32Array} heights
     * @param {number} chunk_size
     * @param {number} offset_x
     * @param {number} offset_y
     * @param {ScatterRequestData} request
     * @returns {ScatterBuild}
     */
    scatter(heights, chunk_size, offset_x, offset_y, request) {
        const ptr0 = passArrayF32ToWasm0(heights, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        _assertClass(request, ScatterRequestData);
        var ptr1 = request.__destroy_into_raw();
        const ret = wasm.terraingenerator_scatter(this.__wbg_ptr, ptr0, len0, chunk_size, offset_x, offset_y, ptr1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ScatterBuild.__wrap(ret[0]);
    }
    /**
     * Writes the inclusive texel window (`x0`, `y0`)–(`x1`, `y1`) of a chunk's
     * splat map into `out`, which holds the whole map. `maskWeights` is the
     * chunk's painted biome mask, if any, with its `maskSize` and `maskStep`.
     * See `terrain::SplatGenerator::generate`.
     * @param {Float32Array} heights
     * @param {number} width
     * @param {number} height
     * @param {number} offset_x
     * @param {number} offset_y
     * @param {Uint8Array | null | undefined} mask_weights
     * @param {number} mask_size
     * @param {number} mask_step
     * @param {number} x0
     * @param {number} y0
     * @param {number} x1
     * @param {number} y1
     * @param {Uint8Array} out
     */
    splat(heights, width, height, offset_x, offset_y, mask_weights, mask_size, mask_step, x0, y0, x1, y1, out) {
        const ptr0 = passArrayF32ToWasm0(heights, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(mask_weights) ? 0 : passArray8ToWasm0(mask_weights, wasm.__wbindgen_malloc);
        var len1 = WASM_VECTOR_LEN;
        var ptr2 = passArray8ToWasm0(out, wasm.__wbindgen_malloc);
        var len2 = WASM_VECTOR_LEN;
        wasm.terraingenerator_splat(this.__wbg_ptr, ptr0, len0, width, height, offset_x, offset_y, ptr1, len1, mask_size, mask_step, x0, y0, x1, y1, ptr2, len2, out);
    }
    /**
     * The water map of a chunk with LOD-0 `heights`, or undefined where no
     * water comes near the ground. See `terrain::water::build_map`.
     * @param {number} chunk_size
     * @param {number} step
     * @param {number} offset_x
     * @param {number} offset_y
     * @param {Float32Array} heights
     * @param {WaterEditData | null} [edit]
     * @returns {WaterMapBuild | undefined}
     */
    waterMap(chunk_size, step, offset_x, offset_y, heights, edit) {
        const ptr0 = passArrayF32ToWasm0(heights, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        let ptr1 = 0;
        if (!isLikeNone(edit)) {
            _assertClass(edit, WaterEditData);
            ptr1 = edit.__destroy_into_raw();
        }
        const ret = wasm.terraingenerator_waterMap(this.__wbg_ptr, chunk_size, step, offset_x, offset_y, ptr0, len0, ptr1);
        return ret === 0 ? undefined : WaterMapBuild.__wrap(ret);
    }
    /**
     * The water of a `chunkSize` chunk at chunk offset (`offsetX`,
     * `offsetY`), one texel every `step` samples, with `edit` over it.
     * @param {number} chunk_size
     * @param {number} step
     * @param {number} offset_x
     * @param {number} offset_y
     * @param {WaterEditData | null} [edit]
     * @returns {ResolvedWaterBuild}
     */
    water(chunk_size, step, offset_x, offset_y, edit) {
        let ptr0 = 0;
        if (!isLikeNone(edit)) {
            _assertClass(edit, WaterEditData);
            ptr0 = edit.__destroy_into_raw();
        }
        const ret = wasm.terraingenerator_water(this.__wbg_ptr, chunk_size, step, offset_x, offset_y, ptr0);
        return ResolvedWaterBuild.__wrap(ret);
    }
}
if (Symbol.dispose) TerrainGenerator.prototype[Symbol.dispose] = TerrainGenerator.prototype.free;

/**
 * A built chunk mesh. Take each array once, then call `free()`.
 */
export class TerrainMeshBuild {
    static __wrap(ptr) {
        const obj = Object.create(TerrainMeshBuild.prototype);
        obj.__wbg_ptr = ptr;
        TerrainMeshBuildFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        TerrainMeshBuildFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_terrainmeshbuild_free(ptr, 0);
    }
    /**
     * @returns {Uint32Array}
     */
    takeIndices() {
        const ret = wasm.terrainmeshbuild_takeIndices(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * @returns {Float32Array}
     */
    takeNormals() {
        const ret = wasm.terrainmeshbuild_takeNormals(this.__wbg_ptr);
        var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * @returns {Float32Array}
     */
    takeUvs() {
        const ret = wasm.terrainmeshbuild_takeUvs(this.__wbg_ptr);
        var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * @returns {Float32Array}
     */
    takeVertices() {
        const ret = wasm.terrainmeshbuild_takeVertices(this.__wbg_ptr);
        var v1 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
}
if (Symbol.dispose) TerrainMeshBuild.prototype[Symbol.dispose] = TerrainMeshBuild.prototype.free;

/**
 * A chunk's water edit, copied in for one call. See `WaterEdit.ts`.
 */
export class WaterEditData {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        WaterEditDataFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_watereditdata_free(ptr, 0);
    }
    /**
     * `weights` holds the mask's channel-major planes.
     * @param {Uint8Array} weights
     * @param {number} size
     * @param {number} step
     * @param {Float32Array} level
     * @param {Uint32Array} body_ids
     */
    constructor(weights, size, step, level, body_ids) {
        const ptr0 = passArray8ToWasm0(weights, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayF32ToWasm0(level, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray32ToWasm0(body_ids, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.watereditdata_new(ptr0, len0, size, step, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        WaterEditDataFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) WaterEditData.prototype[Symbol.dispose] = WaterEditData.prototype.free;

/**
 * A chunk's water map. Take each array once, then call `free()`.
 */
export class WaterMapBuild {
    static __wrap(ptr) {
        const obj = Object.create(WaterMapBuild.prototype);
        obj.__wbg_ptr = ptr;
        WaterMapBuildFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        WaterMapBuildFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_watermapbuild_free(ptr, 0);
    }
    /**
     * @returns {number}
     */
    get baseLevel() {
        const ret = wasm.watermapbuild_baseLevel(this.__wbg_ptr);
        return ret;
    }
    /**
     * The world level at each of `bodyTexels`.
     * @returns {Float64Array}
     */
    bodyLevels() {
        const ret = wasm.watermapbuild_bodyLevels(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * The first texel each covering body owns, in texel order.
     * @returns {Uint32Array}
     */
    bodyTexels() {
        const ret = wasm.watermapbuild_bodyTexels(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The generated lakes that reach the chunk, as `Lake` records.
     * @returns {any}
     */
    lakes() {
        const ret = wasm.watermapbuild_lakes(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {number}
     */
    get maxLevel() {
        const ret = wasm.watermapbuild_maxLevel(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {boolean}
     */
    get shows() {
        const ret = wasm.watermapbuild_shows(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * @returns {Uint32Array}
     */
    takeBodyIds() {
        const ret = wasm.watermapbuild_takeBodyIds(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * @returns {Uint8Array}
     */
    takeCoverage() {
        const ret = wasm.watermapbuild_takeCoverage(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * f16 bits: terrain height − baseLevel.
     * @returns {Uint16Array}
     */
    takeHeights() {
        const ret = wasm.watermapbuild_takeHeights(this.__wbg_ptr);
        var v1 = getArrayU16FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 2, 2);
        return v1;
    }
    /**
     * f16 bits: water surface height − baseLevel.
     * @returns {Uint16Array}
     */
    takeLevel() {
        const ret = wasm.watermapbuild_takeLevel(this.__wbg_ptr);
        var v1 = getArrayU16FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 2, 2);
        return v1;
    }
    /**
     * @returns {Uint8Array}
     */
    takeTypeWeights() {
        const ret = wasm.watermapbuild_takeTypeWeights(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) WaterMapBuild.prototype[Symbol.dispose] = WaterMapBuild.prototype.free;

/**
 * Builds a BVH over `vertices` (flat xyz). See `bvh::build`.
 *
 * `strategy` is `'sah'` or `'center'`, as in `BVHOptions`.
 * @param {Float32Array} vertices
 * @param {Uint32Array | null | undefined} indices
 * @param {string} strategy
 * @param {number} max_depth
 * @param {number} max_leaf_triangles
 * @returns {BvhBuild}
 */
export function buildBvh(vertices, indices, strategy, max_depth, max_leaf_triangles) {
    const ptr0 = passArrayF32ToWasm0(vertices, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    var ptr1 = isLikeNone(indices) ? 0 : passArray32ToWasm0(indices, wasm.__wbindgen_malloc);
    var len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(strategy, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.buildBvh(ptr0, len0, ptr1, len1, ptr2, len2, max_depth, max_leaf_triangles);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return BvhBuild.__wrap(ret[0]);
}

/**
 * The mesh of a `size` × `size` chunk at `lod` over `heights`, which carries
 * `apron` rings of neighbour samples. See `terrain::mesh::build`.
 * @param {Float32Array} heights
 * @param {number} size
 * @param {number} apron
 * @param {number} lod
 * @param {number} horizontal_scale
 * @returns {TerrainMeshBuild}
 */
export function buildTerrainMesh(heights, size, apron, lod, horizontal_scale) {
    const ptr0 = passArrayF32ToWasm0(heights, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.buildTerrainMesh(ptr0, len0, size, apron, lod, horizontal_scale);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return TerrainMeshBuild.__wrap(ret[0]);
}

/**
 * Digs a patch toward a water stroke's bed and returns the (cx, cy) of each
 * chunk it changed. `guard` is the stroke's `WaterGuard` over world texels
 * from (`guardI0`, `guardJ0`), one every `step` samples, and `texel` a
 * texel's width in metres. See `terrain::edit::carve`.
 * @param {Float32Array} heights
 * @param {Uint8Array} flags
 * @param {number} x0
 * @param {number} z0
 * @param {number} width
 * @param {number} span
 * @param {number} meters_per_sample
 * @param {Float32Array} originals
 * @param {Float32Array} targets
 * @param {Uint8Array} guard
 * @param {number} guard_i0
 * @param {number} guard_j0
 * @param {number} guard_width
 * @param {number} step
 * @param {number} texel
 * @param {number} centre_x
 * @param {number} centre_z
 * @param {number} radius
 * @param {number} level
 * @param {number} depth
 * @param {number} blend
 * @returns {Int32Array}
 */
export function carveStamp(heights, flags, x0, z0, width, span, meters_per_sample, originals, targets, guard, guard_i0, guard_j0, guard_width, step, texel, centre_x, centre_z, radius, level, depth, blend) {
    var ptr0 = passArrayF32ToWasm0(heights, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(flags, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayF32ToWasm0(originals, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    var ptr3 = passArrayF32ToWasm0(targets, wasm.__wbindgen_malloc);
    var len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray8ToWasm0(guard, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    const ret = wasm.carveStamp(ptr0, len0, heights, ptr1, len1, x0, z0, width, span, meters_per_sample, ptr2, len2, ptr3, len3, targets, ptr4, len4, guard_i0, guard_j0, guard_width, step, texel, centre_x, centre_z, radius, level, depth, blend);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v6 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v6;
}

/**
 * Raises the bank of a remove stroke over a patch and returns the (cx, cy) of
 * each chunk it changed. `levels` and `covered` are the planes of its
 * `CutGrid`. See `terrain::edit::cut_bank`.
 * @param {Float32Array} heights
 * @param {Uint8Array} flags
 * @param {number} x0
 * @param {number} z0
 * @param {number} width
 * @param {number} span
 * @param {number} meters_per_sample
 * @param {Float64Array} discs
 * @param {Float32Array} levels
 * @param {Uint8Array} covered
 * @param {number} grid_i0
 * @param {number} grid_j0
 * @param {number} grid_width
 * @param {number} step
 * @param {number} texel
 * @param {number} height
 * @returns {Int32Array}
 */
export function cutBank(heights, flags, x0, z0, width, span, meters_per_sample, discs, levels, covered, grid_i0, grid_j0, grid_width, step, texel, height) {
    var ptr0 = passArrayF32ToWasm0(heights, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(flags, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayF64ToWasm0(discs, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArrayF32ToWasm0(levels, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray8ToWasm0(covered, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    const ret = wasm.cutBank(ptr0, len0, heights, ptr1, len1, x0, z0, width, span, meters_per_sample, ptr2, len2, ptr3, len3, ptr4, len4, grid_i0, grid_j0, grid_width, step, texel, height);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v6 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v6;
}

/**
 * Recomputes the bounds of a built BVH's `nodes` in place after `vertices`
 * move. See `bvh::refit`.
 * @param {Uint32Array} nodes
 * @param {Float32Array} vertices
 * @param {Uint32Array} tri_indices
 */
export function refitBvh(nodes, vertices, tri_indices) {
    var ptr0 = passArray32ToWasm0(nodes, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayF32ToWasm0(vertices, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray32ToWasm0(tri_indices, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    wasm.refitBvh(ptr0, len0, nodes, ptr1, len1, ptr2, len2);
}

/**
 * Applies a sculpt stamp to a patch and returns the (cx, cy) of each chunk it
 * changed. `brush` is a `SculptBrushType`. `baseline` holds the generated
 * heights over the patch for `'reset'`, and may be empty otherwise.
 * `target` is the flatten height and `kernel` the smooth half-width. See
 * `terrain::edit::sculpt`.
 * @param {Float32Array} heights
 * @param {Uint8Array} flags
 * @param {number} x0
 * @param {number} z0
 * @param {number} width
 * @param {number} span
 * @param {number} meters_per_sample
 * @param {Float32Array} baseline
 * @param {string} brush
 * @param {number} centre_x
 * @param {number} centre_z
 * @param {number} radius
 * @param {number} amount
 * @param {number} target
 * @param {number} kernel
 * @returns {Int32Array}
 */
export function sculptStamp(heights, flags, x0, z0, width, span, meters_per_sample, baseline, brush, centre_x, centre_z, radius, amount, target, kernel) {
    var ptr0 = passArrayF32ToWasm0(heights, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(flags, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayF32ToWasm0(baseline, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passStringToWasm0(brush, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.sculptStamp(ptr0, len0, heights, ptr1, len1, x0, z0, width, span, meters_per_sample, ptr2, len2, ptr3, len3, centre_x, centre_z, radius, amount, target, kernel);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v5;
}

/**
 * Raises the lip of an add stroke over a patch and returns the (cx, cy) of
 * each chunk it changed. `wet` and `own` are the planes of its `ShoreGrid`.
 * See `terrain::edit::lip`.
 * @param {Float32Array} heights
 * @param {Uint8Array} flags
 * @param {number} x0
 * @param {number} z0
 * @param {number} width
 * @param {number} span
 * @param {number} meters_per_sample
 * @param {Float32Array} originals
 * @param {Float64Array} discs
 * @param {Uint8Array} wet
 * @param {Uint8Array} own
 * @param {number} grid_i0
 * @param {number} grid_j0
 * @param {number} grid_width
 * @param {number} step
 * @param {number} texel
 * @param {number} level
 * @param {number} height
 * @returns {Int32Array}
 */
export function waterLip(heights, flags, x0, z0, width, span, meters_per_sample, originals, discs, wet, own, grid_i0, grid_j0, grid_width, step, texel, level, height) {
    var ptr0 = passArrayF32ToWasm0(heights, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(flags, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayF32ToWasm0(originals, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArrayF64ToWasm0(discs, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray8ToWasm0(wet, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    const ptr5 = passArray8ToWasm0(own, wasm.__wbindgen_malloc);
    const len5 = WASM_VECTOR_LEN;
    const ret = wasm.waterLip(ptr0, len0, heights, ptr1, len1, x0, z0, width, span, meters_per_sample, ptr2, len2, ptr3, len3, ptr4, len4, ptr5, len5, grid_i0, grid_j0, grid_width, step, texel, level, height);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v7 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v7;
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg_Error_30c8987f7c2ed4e2: function(arg0, arg1) {
            const ret = Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_Number_14af1003b8dd5ead: function(arg0) {
            const ret = Number(arg0);
            return ret;
        },
        __wbg_String_8564e559799eccda: function(arg0, arg1) {
            const ret = String(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_bigint_get_as_i64_a2383202b9353e4c: function(arg0, arg1) {
            const v = arg1;
            const ret = typeof(v) === 'bigint' ? v : undefined;
            getDataViewMemory0().setBigInt64(arg0 + 8 * 1, isLikeNone(ret) ? BigInt(0) : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_boolean_get_5b446f51afd21013: function(arg0) {
            const v = arg0;
            const ret = typeof(v) === 'boolean' ? v : undefined;
            return isLikeNone(ret) ? 0xFFFFFF : ret ? 1 : 0;
        },
        __wbg___wbindgen_copy_to_typed_array_88899a52af046901: function(arg0, arg1, arg2) {
            new Uint8Array(arg2.buffer, arg2.byteOffset, arg2.byteLength).set(getArrayU8FromWasm0(arg0, arg1));
        },
        __wbg___wbindgen_debug_string_4687d8d8c2017d52: function(arg0, arg1) {
            const ret = debugString(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_in_92f62ee1427d9e49: function(arg0, arg1) {
            const ret = arg0 in arg1;
            return ret;
        },
        __wbg___wbindgen_is_bigint_b123553bed3bb382: function(arg0) {
            const ret = typeof(arg0) === 'bigint';
            return ret;
        },
        __wbg___wbindgen_is_function_1f9d30630b8b1d3d: function(arg0) {
            const ret = typeof(arg0) === 'function';
            return ret;
        },
        __wbg___wbindgen_is_object_3c45d4f2dde4e749: function(arg0) {
            const val = arg0;
            const ret = typeof(val) === 'object' && val !== null;
            return ret;
        },
        __wbg___wbindgen_is_undefined_8865fb403f8fe9d8: function(arg0) {
            const ret = arg0 === undefined;
            return ret;
        },
        __wbg___wbindgen_jsval_eq_02babf21faa37971: function(arg0, arg1) {
            const ret = arg0 === arg1;
            return ret;
        },
        __wbg___wbindgen_jsval_loose_eq_677f21e468d6b461: function(arg0, arg1) {
            const ret = arg0 == arg1;
            return ret;
        },
        __wbg___wbindgen_number_get_2e0e7dee9f701a71: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'number' ? obj : undefined;
            getDataViewMemory0().setFloat64(arg0 + 8 * 1, isLikeNone(ret) ? 0 : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_string_get_0380ccaa2f57f0d9: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'string' ? obj : undefined;
            var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            var len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_throw_41e9ee4f547fc59a: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbg_call_6137034ef55c9d0f: function() { return handleError(function (arg0, arg1) {
            const ret = arg0.call(arg1);
            return ret;
        }, arguments); },
        __wbg_done_b41a1d26cdb37fb6: function(arg0) {
            const ret = arg0.done;
            return ret;
        },
        __wbg_entries_fb6397112b1de25f: function(arg0) {
            const ret = Object.entries(arg0);
            return ret;
        },
        __wbg_get_658f6698067d9515: function() { return handleError(function (arg0, arg1) {
            const ret = Reflect.get(arg0, arg1);
            return ret;
        }, arguments); },
        __wbg_get_6c896e0571ddae51: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_get_unchecked_288889d017702237: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_get_with_ref_key_6412cf3094599694: function(arg0, arg1) {
            const ret = arg0[arg1];
            return ret;
        },
        __wbg_instanceof_ArrayBuffer_a99f175873e5d9b8: function(arg0) {
            let result;
            try {
                result = arg0 instanceof ArrayBuffer;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_Map_b2611749102d7ba3: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Map;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_Uint8Array_828cef2aaacafc31: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Uint8Array;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_isArray_e15a2ff68ffdbef2: function(arg0) {
            const ret = Array.isArray(arg0);
            return ret;
        },
        __wbg_isSafeInteger_717808ad6a54bd9e: function(arg0) {
            const ret = Number.isSafeInteger(arg0);
            return ret;
        },
        __wbg_iterator_e3c31c892080e444: function() {
            const ret = Symbol.iterator;
            return ret;
        },
        __wbg_length_7f3c00c40364105e: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_length_d4bdea10311bd9cf: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_new_1dbf7428bba60a42: function(arg0) {
            const ret = new Uint8Array(arg0);
            return ret;
        },
        __wbg_new_617a8cdb8bb1130e: function() {
            const ret = new Object();
            return ret;
        },
        __wbg_new_ee2291f50781bf1d: function() {
            const ret = new Array();
            return ret;
        },
        __wbg_next_33784799010f1bbe: function(arg0) {
            const ret = arg0.next;
            return ret;
        },
        __wbg_next_f4aac29c42af995c: function() { return handleError(function (arg0) {
            const ret = arg0.next();
            return ret;
        }, arguments); },
        __wbg_prototypesetcall_bc27214492979395: function(arg0, arg1, arg2) {
            Uint8Array.prototype.set.call(getArrayU8FromWasm0(arg0, arg1), arg2);
        },
        __wbg_set_6be42768c690e380: function(arg0, arg1, arg2) {
            arg0[arg1] = arg2;
        },
        __wbg_set_bea140a88be9b277: function(arg0, arg1, arg2) {
            arg0[arg1 >>> 0] = arg2;
        },
        __wbg_value_f3c585ee8f5ba40c: function(arg0) {
            const ret = arg0.value;
            return ret;
        },
        __wbindgen_generic_0000000000000001: function(arg0) {
            // Cast intrinsic for `F64 -> Externref`.
            const ret = arg0;
            return ret;
        },
        __wbindgen_generic_0000000000000002: function(arg0) {
            // Cast intrinsic for `I64 -> Externref`.
            const ret = arg0;
            return ret;
        },
        __wbindgen_generic_0000000000000003: function(arg0, arg1) {
            // Cast intrinsic for `Ref(String) -> Externref`.
            const ret = getStringFromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_generic_0000000000000004: function(arg0) {
            // Cast intrinsic for `U64 -> Externref`.
            const ret = BigInt.asUintN(64, arg0);
            return ret;
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./rewild_wasm_bg.js": import0,
    };
}

const BvhBuildFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_bvhbuild_free(ptr, 1));
const FarMapBuildFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_farmapbuild_free(ptr, 1));
const ResolvedWaterBuildFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_resolvedwaterbuild_free(ptr, 1));
const ScatterBuildFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_scatterbuild_free(ptr, 1));
const ScatterRequestDataFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_scatterrequestdata_free(ptr, 1));
const TerrainGeneratorFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_terraingenerator_free(ptr, 1));
const TerrainMeshBuildFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_terrainmeshbuild_free(ptr, 1));
const WaterEditDataFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_watereditdata_free(ptr, 1));
const WaterMapBuildFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_watermapbuild_free(ptr, 1));

function addToExternrefTable0(obj) {
    const idx = wasm.__externref_table_alloc();
    wasm.__wbindgen_externrefs.set(idx, obj);
    return idx;
}

function _assertClass(instance, klass) {
    if (!(instance instanceof klass)) {
        throw new Error(`expected instance of ${klass.name}`);
    }
}

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function getArrayF32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayF64FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat64ArrayMemory0().subarray(ptr / 8, ptr / 8 + len);
}

function getArrayI32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getInt32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU16FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint16ArrayMemory0().subarray(ptr / 2, ptr / 2 + len);
}

function getArrayU32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

let cachedFloat32ArrayMemory0 = null;
function getFloat32ArrayMemory0() {
    if (cachedFloat32ArrayMemory0 === null || cachedFloat32ArrayMemory0.byteLength === 0) {
        cachedFloat32ArrayMemory0 = new Float32Array(wasm.memory.buffer);
    }
    return cachedFloat32ArrayMemory0;
}

let cachedFloat64ArrayMemory0 = null;
function getFloat64ArrayMemory0() {
    if (cachedFloat64ArrayMemory0 === null || cachedFloat64ArrayMemory0.byteLength === 0) {
        cachedFloat64ArrayMemory0 = new Float64Array(wasm.memory.buffer);
    }
    return cachedFloat64ArrayMemory0;
}

let cachedInt32ArrayMemory0 = null;
function getInt32ArrayMemory0() {
    if (cachedInt32ArrayMemory0 === null || cachedInt32ArrayMemory0.byteLength === 0) {
        cachedInt32ArrayMemory0 = new Int32Array(wasm.memory.buffer);
    }
    return cachedInt32ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint16ArrayMemory0 = null;
function getUint16ArrayMemory0() {
    if (cachedUint16ArrayMemory0 === null || cachedUint16ArrayMemory0.byteLength === 0) {
        cachedUint16ArrayMemory0 = new Uint16Array(wasm.memory.buffer);
    }
    return cachedUint16ArrayMemory0;
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function handleError(f, args) {
    try {
        return f.apply(this, args);
    } catch (e) {
        const idx = addToExternrefTable0(e);
        wasm.__wbindgen_exn_store(idx);
    }
}

function isLikeNone(x) {
    return x === undefined || x === null;
}

function passArray32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getUint32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getFloat32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF64ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 8, 8) >>> 0;
    getFloat64ArrayMemory0().set(arg, ptr / 8);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedDataViewMemory0 = null;
    cachedFloat32ArrayMemory0 = null;
    cachedFloat64ArrayMemory0 = null;
    cachedInt32ArrayMemory0 = null;
    cachedUint16ArrayMemory0 = null;
    cachedUint32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (!module.ok) {
            throw new Error(`failed to fetch Wasm: ${module.status} ${module.statusText} fetching '${module.url}'`);
        }

        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('rewild_wasm_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
